import type { ReactNode } from "react";

import { cn } from "../../lib/cn";

type ParagraphSize = "xxlarge" | "xlarge" | "large" | "medium" | "small" | "xsmall";
type ParagraphWeight = "regular" | "medium-bold" | "bold";
type ParagraphColor =
    | "primary"
    | "inverse-primary"
    | "secondary"
    | "inverse-secondary"
    | "tertiary"
    | "highlight";

export type ParagraphProps = {
    className?: string;
    children: ReactNode;
    size?: ParagraphSize;
    weight?: ParagraphWeight;
    color?: ParagraphColor;
};

export function Paragraph({
    className,
    children,
    size = "medium",
    weight = "regular",
    color = "primary",
}: ParagraphProps) {
    return (
        <p
            className={cn(
                paragraphBaseClass,
                paragraphSizeClass[size],
                paragraphWeightClass[weight],
                paragraphColorClass[color],
                className,
            )}
        >
            {children}
        </p>
    );
}

const paragraphBaseClass = "m-0 whitespace-pre-wrap p-0";

const paragraphSizeClass: Record<ParagraphSize, string> = {
    xxlarge:
        "font-paragraph-large tracking-paragraph-large text-mobile-paragraph-xxlarge leading-mobile-paragraph-xxlarge sm-md:text-desktop-paragraph-xxlarge sm-md:leading-desktop-paragraph-xxlarge",
    xlarge: "font-paragraph-large tracking-paragraph-large text-mobile-paragraph-xlarge leading-mobile-paragraph-xlarge sm-md:text-desktop-paragraph-xlarge sm-md:leading-desktop-paragraph-xlarge",
    large: "font-paragraph-large tracking-paragraph-large text-mobile-paragraph-large leading-mobile-paragraph-large sm-md:text-desktop-paragraph-large sm-md:leading-desktop-paragraph-large",
    medium: "font-paragraph-base tracking-paragraph-base text-mobile-paragraph-medium leading-mobile-paragraph-medium sm-md:text-desktop-paragraph-medium sm-md:leading-desktop-paragraph-medium",
    small: "font-paragraph-base tracking-paragraph-base text-mobile-paragraph-small leading-mobile-paragraph-small sm-md:text-desktop-paragraph-small sm-md:leading-desktop-paragraph-small",
    xsmall: "font-paragraph-base tracking-paragraph-base text-mobile-paragraph-xsmall leading-mobile-paragraph-xsmall sm-md:text-desktop-paragraph-xsmall sm-md:leading-desktop-paragraph-xsmall",
};

const paragraphColorClass: Record<ParagraphColor, string> = {
    primary: "text-text-primary",
    "inverse-primary": "text-text-inverse-primary",
    secondary: "text-text-secondary",
    "inverse-secondary": "text-text-inverse-secondary",
    tertiary: "text-text-tertiary",
    highlight: "text-text-highlight",
};

const paragraphWeightClass: Record<ParagraphWeight, string> = {
    regular: "font-weight-paragraph-regular",
    "medium-bold": "font-weight-paragraph-medium",
    bold: "font-weight-paragraph-bold",
};
